import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { HoverInsight } from '../types';
import MetricCard from '../components/MetricCard';
import api from '../lib/api';
import { DateRangePicker } from '../components/DateRangePicker';
import { useGlobalDateRange, clampRangeToBounds } from '../context/GlobalDateRangeContext';
import { formatChartDate } from '../lib/formatDate';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';

const BLUE = '#1d4ed8';
const SKY = '#0ea5e9';

interface DtcKpisProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface PeriodStats {
  start_date: string;
  end_date: string;
  sessions: number;
  revenue: number;
}

interface GA4Live {
  connected: boolean;
  fetched_at?: string;
  account_name: string;
  current: PeriodStats;
  previous: PeriodStats;
  sessions_change_pct: number | null;
  revenue_change_pct: number | null;
  revenue_per_session: number;
  daily_trend: { date: string; sessions: number; revenue: number }[];
  traffic_sources: { channel: string; sessions: number; revenue: number }[];
  ga4_google_ads_clicks: number;
  ga4_google_ads_cost: number;
}

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const toISO = (d: Date) => format(d, 'yyyy-MM-dd');
const fmtDate = (s: string) => formatChartDate(s);

const changeLabel = (pct: number | null) => (pct === null ? 'No change vs previous' : `${Math.abs(pct).toFixed(1)}% ${pct >= 0 ? 'up' : 'down'} vs previous`);
const changeTrend = (pct: number | null): 'up' | 'down' | 'neutral' => (pct === null ? 'neutral' : pct >= 0 ? 'up' : 'down');


export default function DtcKpis({ onHover }: DtcKpisProps) {
  const [data, setData] = useState<GA4Live | null>(null);
  const [loading, setLoading] = useState(true);

  const today = useMemo(() => new Date(), []);
  const sixtyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 59); return d; }, []);
  const { globalRange } = useGlobalDateRange();
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => clampRangeToBounds(globalRange, sixtyDaysAgo, today, 60));

  useEffect(() => {
    setLoading(true);
    // Live Windsor.ai GA4 query on every request (no Mongo cache), plus a 2nd live query for the
    // auto-computed previous period of equal length — powers the Comparison Window below.
    api.get('/dtc-kpis/live', { params: { start_date: toISO(range.from), end_date: toISO(range.to) } })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [range]);

  const trendData = useMemo(() => (data?.daily_trend || []).map((d) => ({
    date: fmtDate(d.date),
    sessions: d.sessions,
    revenue: d.revenue,
  })), [data]);

  const accountName = data?.account_name || 'NOOE India';

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="dtc-kpis-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">
            GA4 Site Performance
          </span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">
            DTC KPIs
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">
            Live GA4 data for {accountName}, queried directly from Windsor.ai and compared against the previous period of equal length.
          </p>
        </div>
        <DateRangePicker value={range} onChange={setRange} minDate={sixtyDaysAgo} maxDate={today} maxSpanDays={60} testIdPrefix="dtc-kpis" />
      </div>

      {loading ? (
        <div data-testid="dtc-kpis-loading" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">
          Querying Windsor.ai live…
        </div>
      ) : !data?.connected ? (
        <div data-testid="dtc-kpis-disconnected" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">
          No Windsor.ai GA4 data available yet. Ask your admin to confirm the GA4 connection.
        </div>
      ) : (
        <>
          <div data-testid="dtc-kpis-comparison-window" className="bg-white border border-[#e8dcc9] rounded-3xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs">
            <span className="font-mono uppercase text-slate-400 text-[10px]">Comparison Window:</span>
            <span className="font-semibold text-slate-700">{fmtDate(data.current.start_date)} — {fmtDate(data.current.end_date)}</span>
            <span className="text-slate-300">vs.</span>
            <span className="font-semibold text-slate-500">{fmtDate(data.previous.start_date)} — {fmtDate(data.previous.end_date)}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              platform="GA4"
              title="Site Sessions"
              value={data.current.sessions.toLocaleString('en-IN')}
              change={changeLabel(data.sessions_change_pct)}
              trend={changeTrend(data.sessions_change_pct)}
              insight={{ title: 'GA4 Sessions', metric: 'Sessions', value: data.current.sessions.toLocaleString('en-IN'), aiInsight: `${data.current.sessions.toLocaleString('en-IN')} site sessions in the selected window, vs. ${data.previous.sessions.toLocaleString('en-IN')} in the previous period.`, prediction: 'Trend requires more history to project.', nextSteps: ['Review the daily sessions trend below.'] }}
              onHover={onHover}
            />
            <MetricCard
              platform="GA4"
              title="E-commerce Purchase Revenue"
              value={inr(data.current.revenue)}
              change={changeLabel(data.revenue_change_pct)}
              trend={changeTrend(data.revenue_change_pct)}
              insight={{ title: 'E-commerce Purchase Revenue', metric: 'Revenue', value: inr(data.current.revenue), aiInsight: `${inr(data.current.revenue)} in GA4-tracked purchase revenue this window, vs. ${inr(data.previous.revenue)} in the previous period. A ₹0 value reflects GA4's own ecommerce tracking, not a data gap on our side.`, prediction: 'Trend requires more history to project.', nextSteps: ['Verify GA4 ecommerce tracking setup if this stays at ₹0.'] }}
              onHover={onHover}
            />
            <MetricCard
              platform="GA4"
              title="Revenue per Session"
              value={inr(data.revenue_per_session)}
              change="Selected window"
              trend="neutral"
              insight={{ title: 'Revenue per Session', metric: 'Revenue / Session', value: inr(data.revenue_per_session), aiInsight: `Blended purchase revenue per site session in the selected window.`, prediction: 'Trend requires more history to project.', nextSteps: ['Compare against ad spend in the Google Ads / Meta Ads modules.'] }}
              onHover={onHover}
            />
            <MetricCard
              platform="GA4"
              title="Google Ads Cost (via GA4 link)"
              value={inr(data.ga4_google_ads_cost)}
              change={`${data.ga4_google_ads_clicks.toLocaleString('en-IN')} clicks`}
              trend="neutral"
              insight={{ title: 'Google Ads Cost — GA4-Linked', metric: 'Ad Cost (GA4 import)', value: inr(data.ga4_google_ads_cost), aiInsight: `GA4's own Google Ads cost/click import for this window — a cross-check against the dedicated Google Ads module, not blended into revenue above.`, prediction: 'Trend requires more history to project.', nextSteps: ['Cross-check against the Google Ads module\'s spend for the same window.'] }}
              onHover={onHover}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="dtc-kpis-trend-chart">
              <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Daily Sessions vs. E-commerce Purchase Revenue</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Line type="monotone" dataKey="sessions" stroke={SKY} strokeWidth={2.5} dot={false} name="Sessions" />
                    <Line type="monotone" dataKey="revenue" stroke={BLUE} strokeWidth={2.5} dot={false} name="Revenue (₹)" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="dtc-kpis-traffic-sources-chart">
              <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Traffic Source Breakdown</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.traffic_sources} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" stroke="#64748b" fontSize={10} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="channel" stroke="#64748b" fontSize={9.5} tickLine={false} width={95} />
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} formatter={(v: any) => [`${v} sessions`, 'Sessions']} />
                    <Bar dataKey="sessions" radius={[0, 6, 6, 0]} name="Sessions">
                      {data.traffic_sources.map((_, i) => <Cell key={i} fill={i % 2 === 0 ? BLUE : SKY} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {data.traffic_sources.length === 0 && <p className="text-xs text-slate-400 font-mono">No sessions recorded in this window.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
